import { Canvas } from 'fabric/fabric-impl';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { fabric } from 'fabric-with-gestures';
import { EditorStore, resetStore, useStore } from '../store/createStore';
import { CanvasHandler } from '../handlers/CanvasHandler';
import '../util/canvasHistory';

interface WorkArea {
  height: number;
  width: number;
}

function useCanvas(workArea: WorkArea): [Canvas | null, React.RefObject<HTMLCanvasElement>] {
  const [canvas, setCanvas] = useStore<EditorStore, Canvas | null>('canvas');
  const [canvasHandler, setCanvasHandler] = useState<CanvasHandler | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const fabricCanvas = new fabric.Canvas(canvasRef.current);
    // Evita que los objetos seleccionados dentro del canvas se superpongan temporalmente cuando se seleccionan
    fabricCanvas.preserveObjectStacking = true;
    fabricCanvas.allowTouchScrolling = true;
    setCanvasHandler(new CanvasHandler(fabricCanvas));
  }, []);

  useEffect(() => {
    if (!canvasHandler) {
      return;
    }

    const handleResize = () => canvasHandler?.resize(workArea, false);
    const canvasInstance = canvasHandler.canvas;

    canvasHandler?.resize(workArea).finally(() => {
      /**
       * La historía dentro del canvas debe ser inicializada luego de la ejecución del handleResize
       * debido a que la historía no debe considerar como parte de esta la configuración del background o área de trabajo
       * del canvas. Esta configuración se lleva a cabo dentro del handleResize.
       */
      // @ts-ignore
      canvasInstance.initHistory();
      window.addEventListener('resize', handleResize, false);

      // Se configura la instancia del canvas en este punto, con el objetivo de que para este momento el área de trabajo
      // y las dimensiones del canvas ya estén ajustadas. Adicional a esto, también se configura en este punto para
      // que al momento de solicitar algún otro componente el valor del
      // background del canvas (también conocido como, workArea) lo encuentre configurado
      setCanvas(canvasInstance);
    });

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [canvasHandler]);

  useLayoutEffect(() => {
    if (!canvas) {
      return;
    }

    return () => {
      // Limpia la instancia del canvas
      // @ts-ignore
      canvas.dispose();

      // Se limpia el estado de la instancia del canvas en el Store para obligar la creación de una nueva instancia
      // la próxima vez que se ingrese al editor
      setCanvas(undefined);

      // Restaura el Store
      resetStore();
    };
  }, [canvas]);

  return [canvas, canvasRef];
}

export default useCanvas;
